import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Calendar, AtSign, AlertTriangle, CheckCircle2, Clock, UserCheck } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import TaskAttachments from "@/components/TaskAttachments";
import { renderMentions } from "@/lib/mentions.jsx";

const PRIORITY = {
  baixa: { label: "Baixa", color: "#94A3B8", bg: "#F1F5F9" },
  normal: { label: "Normal", color: "#3B82F6", bg: "#DBEAFE" },
  alta: { label: "Alta", color: "#DC2626", bg: "#FEE2E2" },
};

const AREAS = [
  { value: "recepcao", label: "Recepção" },
  { value: "gerencia", label: "Gerência" },
  { value: "atendimento", label: "Atendimento" },
  { value: "geral", label: "Geral" },
];

const DEFAULT_FILTERS = { status: "pendente", assignee_user_id: "all", area: "all" };

export default function Tasks() {
  const { apiCall, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "normal", client_id: "", area: "", assignee_user_id: user?.user_id || "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== "all") params.append("status", filters.status);
      if (filters.assignee_user_id !== "all") params.append("assignee_user_id", filters.assignee_user_id);
      if (filters.area !== "all") params.append("area", filters.area);
      const [tRes, cRes, mRes] = await Promise.all([
        apiCall("get", `/tasks?${params}`),
        apiCall("get", "/clients"),
        apiCall("get", "/team/members"),
      ]);
      setTasks(tRes.data);
      setClients(cRes.data);
      setTeam(mRes.data);
    } catch {
      toast.error("Erro ao carregar tarefas");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters]);

  const handleOpen = (task = null) => {
    setEditing(task);
    setForm(
      task
        ? {
            title: task.title || "",
            description: task.description || "",
            due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : "",
            priority: task.priority || "normal",
            client_id: task.client_id || "",
            area: task.area || "",
            assignee_user_id: task.assignee_user_id || "",
          }
        : { title: "", description: "", due_date: "", priority: "normal", client_id: "", area: "", assignee_user_id: user?.user_id || "" }
    );
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        description: form.description,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        priority: form.priority,
        client_id: form.client_id || null,
        area: form.area || null,
        assignee_user_id: form.assignee_user_id || null,
      };
      if (editing) {
        await apiCall("put", `/tasks/${editing.task_id}`, payload);
        toast.success("Tarefa atualizada");
      } else {
        await apiCall("post", "/tasks", payload);
        toast.success("Tarefa criada");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeTask, setCompleteTask] = useState(null);
  const [completeForm, setCompleteForm] = useState({ note: "", image: "" });
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenTask, setReopenTask] = useState(null);
  const [reopenForm, setReopenForm] = useState({ urgency: "ajustar", reason: "" });

  const isManager = user?.role === "master" || user?.team_role === "gerencia";

  const handleStartComplete = (task) => {
    setCompleteTask(task);
    setCompleteForm({ note: "", image: "" });
    setCompleteOpen(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCompleteForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmitComplete = async () => {
    try {
      await apiCall("post", `/tasks/${completeTask.task_id}/complete`, {
        completion_note: completeForm.note || null,
        completion_image: completeForm.image || null,
      });
      toast.success(isManager ? "Tarefa concluída" : "Enviada para aprovação ✓");
      setCompleteOpen(false);
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const handleApprove = async (task) => {
    try {
      await apiCall("post", `/tasks/${task.task_id}/approve`);
      toast.success("Aprovada — tarefa concluída");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const handleStartReopen = (task) => {
    setReopenTask(task);
    setReopenForm({ urgency: "ajustar", reason: "" });
    setReopenOpen(true);
  };

  const handleSubmitReopen = async () => {
    try {
      await apiCall("post", `/tasks/${reopenTask.task_id}/reopen`, reopenForm);
      toast.success("Tarefa reaberta");
      setReopenOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const handleToggleStatus = (task) => {
    if (task.status === "pendente") {
      handleStartComplete(task);
    } else if (isManager) {
      // Reabrir direto
      handleStartReopen(task);
    } else {
      toast.error("Apenas master ou gerência podem reabrir tarefas concluídas");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esta tarefa?")) return;
    try {
      await apiCall("delete", `/tasks/${id}`);
      toast.success("Excluída");
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const now = Date.now();
  const isOverdue = (t) => t.status === "pendente" && t.due_date && new Date(t.due_date).getTime() < now;

  const clientsMap = useMemo(() => {
    const m = {};
    for (const c of clients) m[c.client_id] = c;
    return m;
  }, [clients]);

  const ordered = useMemo(() => tasks, [tasks]);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <PageHeader overline="Operações" title="Bloco de Tarefas" subtitle="Use =funcionario para atribuir login · @cliente para vincular · +externo para fornecedores">
        <Button onClick={() => handleOpen(null)} data-testid="add-task-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nova Tarefa
        </Button>
      </PageHeader>

      {/* Filtros */}
      <Card className="border border-border bg-card p-4 rounded-md">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pendente">Pendentes</SelectItem>
                <SelectItem value="aguardando_aprovacao">Aguardando aprovação</SelectItem>
                <SelectItem value="concluido">Concluídas</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Responsável</Label>
            <Select value={filters.assignee_user_id} onValueChange={(v) => setFilters({ ...filters, assignee_user_id: v })}>
              <SelectTrigger data-testid="filter-assignee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="me">= Apenas minhas ({user?.name})</SelectItem>
                <SelectItem value="unassigned">Sem responsável</SelectItem>
                {team.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>= {m.nickname || m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Área</Label>
            <Select value={filters.area} onValueChange={(v) => setFilters({ ...filters, area: v })}>
              <SelectTrigger data-testid="filter-area"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {AREAS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" size="sm" onClick={() => setFilters(DEFAULT_FILTERS)} data-testid="clear-filters">Limpar</Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card className="border border-border p-12 text-center text-muted-foreground">Carregando...</Card>
      ) : ordered.length === 0 ? (
        <Card className="border border-border p-12 text-center">
          <div className="flex flex-col items-center gap-2">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" strokeWidth={1.2} />
            <p className="text-sm text-muted-foreground">Nada por aqui ✨</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {ordered.map((t) => {
            const p = PRIORITY[t.priority] || PRIORITY.normal;
            const overdue = isOverdue(t);
            return (
              <Card
                key={t.task_id}
                className="border bg-card p-4 rounded-md flex items-start gap-3"
                style={{ borderColor: overdue ? "#FCA5A5" : undefined, background: overdue ? "#FEF2F2" : undefined }}
                data-testid={`task-row-${t.task_id}`}
              >
                <Checkbox
                  checked={t.status === "concluido"}
                  onCheckedChange={() => handleToggleStatus(t)}
                  data-testid={`task-toggle-${t.task_id}`}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-base ${t.status === "concluido" ? "line-through text-muted-foreground" : "text-foreground font-medium"}`}>
                      {t.title}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded" style={{ background: p.bg, color: p.color }}>{p.label}</span>
                    {t.area && (
                      <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                        {AREAS.find((a) => a.value === t.area)?.label || t.area}
                      </span>
                    )}
                    {t.status === "aguardando_aprovacao" && (
                      <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-800">
                        Aguardando aprovação
                      </span>
                    )}
                    {t.reopen_urgency && (
                      <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700" title={t.reopen_reason || ""}>
                        Reaberta · {t.reopen_urgency}
                      </span>
                    )}
                    {t.assignee_name && (
                      <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 flex items-center gap-1">
                        <UserCheck className="h-3 w-3" strokeWidth={1.5} /> {t.assignee_name}
                      </span>
                    )}
                    {overdue && (
                      <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" strokeWidth={1.5} /> Atrasada
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap">{renderMentions(t.description, t.mentions_typed, clientsMap)}</div>
                  )}
                  <TaskAttachments
                    task={t}
                    onChange={(atts) => {
                      setTasks((prev) => prev.map((x) => x.task_id === t.task_id ? { ...x, attachments: atts } : x));
                    }}
                  />
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                    {t.due_date && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" strokeWidth={1.5} />
                        {new Date(t.due_date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    )}
                    {t.client_name && (
                      <span className="flex items-center gap-1">
                        <AtSign className="h-3 w-3" strokeWidth={1.5} /> {t.client_name}
                      </span>
                    )}
                    <span className="ml-auto">Por {t.created_by_name}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {t.status === "aguardando_aprovacao" && isManager && (
                    <Button variant="outline" size="sm" onClick={() => handleApprove(t)} className="text-emerald-700 border-emerald-200" data-testid={`task-approve-${t.task_id}`}>
                      ✓ Aprovar
                    </Button>
                  )}
                  {(t.status === "concluido" || t.status === "aguardando_aprovacao") && (isManager || t.created_by_user_id === user?.user_id) && (
                    <Button variant="ghost" size="sm" onClick={() => handleStartReopen(t)} className="text-amber-700" data-testid={`task-reopen-${t.task_id}`}>
                      ↺ Reabrir
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => handleOpen(t)} data-testid={`task-edit-${t.task_id}`}>
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  {(isManager || t.created_by_user_id === user?.user_id) && (
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(t.task_id)} data-testid={`task-delete-${t.task_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              {editing ? "Editar Tarefa" : "Nova Tarefa"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Ex: Confirmar recebimento de coloração" data-testid="task-title-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Descrição / rascunho</Label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                placeholder={"Detalhes... use:\n=manu (funcionário com login)\n@ana (cliente cadastrada)\n+fornecedor-x (sem login)"}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="task-description-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                <span className="bg-blue-100 text-blue-800 px-1 rounded">=login</span>{" "}
                <span className="bg-amber-100 text-amber-800 px-1 rounded">@cliente</span>{" "}
                <span className="bg-gray-200 text-gray-800 px-1 rounded">+externo</span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest flex items-center gap-1"><Calendar className="h-3 w-3" strokeWidth={1.5} /> Prazo</Label>
                <Input type="datetime-local" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Área</Label>
                <Select value={form.area || "none"} onValueChange={(v) => setForm({ ...form, area: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="task-area-select"><SelectValue placeholder="Geral" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem área</SelectItem>
                    {AREAS.map((a) => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Responsável (login)</Label>
                <Select value={form.assignee_user_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_user_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Sem responsável" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem responsável</SelectItem>
                    {team.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>= {m.nickname || m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Cliente vinculado (opcional)</Label>
              <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-client-select"><SelectValue placeholder="Sem vínculo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem vínculo</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="task-save-button">{editing ? "Salvar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              {isManager ? "Concluir tarefa" : "Enviar para aprovação"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Anotações da conclusão</Label>
              <textarea
                value={completeForm.note}
                onChange={(e) => setCompleteForm({ ...completeForm, note: e.target.value })}
                rows={3}
                placeholder="O que foi feito, observações, próximos passos..."
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                data-testid="complete-note-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Foto comprobatória (opcional)</Label>
              <Input type="file" accept="image/*" onChange={handleImageUpload} data-testid="complete-image-input" />
              {completeForm.image && (
                <img src={completeForm.image} alt="preview" className="mt-2 rounded-md max-h-40" />
              )}
            </div>
            {!isManager && (
              <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                Esta tarefa irá para aprovação. Master ou Gerência precisam validar antes da conclusão final.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setCompleteOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={handleSubmitComplete} data-testid="complete-submit">
              {isManager ? "Concluir" : "Enviar p/ aprovação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Reabrir / Pedir refazer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Motivo</Label>
              <Select value={reopenForm.urgency} onValueChange={(v) => setReopenForm({ ...reopenForm, urgency: v })}>
                <SelectTrigger data-testid="reopen-urgency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nao_concluida">Não foi concluída</SelectItem>
                  <SelectItem value="errado">Concluída errada</SelectItem>
                  <SelectItem value="ajustar">Precisa ajustar</SelectItem>
                  <SelectItem value="alterar_comando">Alterar comando</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Detalhes (opcional)</Label>
              <textarea
                value={reopenForm.reason}
                onChange={(e) => setReopenForm({ ...reopenForm, reason: e.target.value })}
                rows={3}
                placeholder="O que precisa ser refeito ou corrigido..."
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                data-testid="reopen-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setReopenOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={handleSubmitReopen} data-testid="reopen-submit">Reabrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
